import React from 'react';
import { FormGroup } from '../../designSystem/Form';
import TextFieldStatus from '@/designSystem/TextFieldStatus';
import TextField from '../../designSystem/TextField';
import componentsId from '@/utils/componentsId';
import { formStepTexts } from '@/utils/generalTexts';
import { regExpRut, regExpSpecialCharacters } from '@/utils/constants';
import { sanitizeCompanyName } from '@/utils/validations';

interface CompanyInfoFormProps {
  readonly companyRut: string;
  readonly companyName: string;
  readonly formErrors: Record<string, { hasError: boolean; feedback: string }>;
  readonly availableInput: { companyName: boolean; form: boolean };
  readonly loading: boolean;
  readonly handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  readonly handleBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
  readonly handleKeyPress: (event: React.KeyboardEvent<HTMLInputElement>, regEx: RegExp) => void;
  readonly formatRutOnBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
}

function CompanyInfoForm({
  companyRut,
  companyName,
  formErrors,
  availableInput,
  loading,
  handleChange,
  handleBlur,
  handleKeyPress,
  formatRutOnBlur,
}: CompanyInfoFormProps) {

  const handleCompanyNameBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const sanitized = sanitizeCompanyName(event.target.value);
    if (sanitized !== event.target.value) {
      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        'value'
      )?.set;
      nativeInputValueSetter?.call(event.target, sanitized);
      const changeEvent = new Event('input', { bubbles: true });
      event.target.dispatchEvent(changeEvent);
    }
    handleBlur(event);
  };

  return (
    <>
      <FormGroup>
        <TextFieldStatus
          id={componentsId.formStep.inputCompanyRut}
          label={formStepTexts.inputRut}
          value={companyRut || ''}
          onChange={(event: any) => {
            formatRutOnBlur(event);
            handleChange(event);
          }}
          onKeyPress={(event) => handleKeyPress(event, regExpRut)}
          onBlur={handleBlur}
          hasError={formErrors[componentsId.formStep.inputCompanyRut]?.hasError}
          feedback={formErrors[componentsId.formStep.inputCompanyRut]?.feedback}
          loading={loading}
          {...{
            name: 'rut',
            autoComplete: 'nope',
          }}
        />
      </FormGroup>
      <FormGroup>
        <TextField
          id={componentsId.formStep.inputCompanyName}
          label={formStepTexts.inputName}
          value={companyName || ''}
          onChange={handleChange}
          disabled={availableInput.companyName}
          onBlur={handleCompanyNameBlur}
          hasError={formErrors[componentsId.formStep.inputCompanyName]?.hasError}
          feedback={formErrors[componentsId.formStep.inputCompanyName]?.feedback}
          onKeyPress={(event) => handleKeyPress(event, regExpSpecialCharacters)}
          maxLength={250}
        />
      </FormGroup>
    </>
  );
}

export default CompanyInfoForm;
